import React, { memo, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  styled,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Check, Copy, Info } from '@phosphor-icons/react';
import theme from '../../theme';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';

// Styled components
const ChatContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  height: '100%',
  overflowY: 'auto',
  backgroundColor: '#FFF',
  // '&::-webkit-scrollbar': {
  //   display: 'none',
  // },
  // msOverflowStyle: 'none',
  // scrollbarWidth: 'none',
});

const MessageContainer = styled(Paper)({
  padding: '16px',
  marginBottom: '12px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: '#FFF',
  position: 'relative',
});

const RoleLabel = styled(Typography)<{ role: string }>(({ theme, role }) => ({
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
  color: role === 'user' ? '#FF4D6A' : theme.palette.primary.main,
}));

const MessageContent = styled(Typography)({
  color: '#2B2D3F',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '-0.16px',
});

const MessageFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '8px',
  fontSize: '12px',
  color: theme.palette.lightGray.text,
});

interface ChatMessage {
  message: string;
  role: string;
  timestamp: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  error?: null | string;
  latency?: number;
  status?: string;
}

interface DetailRowProps {
  label: string;
  value: string | number;
  copyable?: boolean;
  tooltip?: string;
}

export interface WhatsappConversationProps {
  messages: ChatMessage[];
  phoneNumber: string;
  whatsappPhonenumberId: string;
  isLoading: boolean;
  formatDate: (dateString: string) => string;
}

interface TokenStats {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  averageLatency: number;
}

const DetailRow = memo<DetailRowProps>(
  ({ label, value, copyable, tooltip }) => {
    const { copy } = useCopyToClipboard();
    const [copied, setCopied] = React.useState(false);

    const handleCopyClick = useCallback(() => {
      copy(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }, [value, copy]);

    const content = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
        }}
      >
        <Typography
          sx={{
            color: '#6B7280',
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {label}
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <IconButton size="small" sx={{ mx: 0.5, p: 0 }}>
                <Info size={14} />
              </IconButton>
            </Tooltip>
          )}
          :
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            sx={{
              color: '#111827',
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            {value}
          </Typography>
          {copyable && (
            <IconButton size="small" sx={{ p: 0.5 }} onClick={handleCopyClick}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </IconButton>
          )}
        </Box>
      </Box>
    );

    return content;
  }
);

const calculateTokens = (messages: ChatMessage[]): TokenStats => {
  if (!Array.isArray(messages)) {
    return {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
      averageLatency: 0,
    };
  }

  const stats = messages.reduce(
    (acc, message) => {
      if (message.role === 'user' && message.prompt_tokens) {
        acc.promptTokens += message.prompt_tokens;
      }
      if (message.role === 'assistant' && message.completion_tokens) {
        acc.completionTokens += message.completion_tokens;
        if (message.latency) {
          acc.totalLatency += message.latency;
          acc.latencyCount += 1;
        }
      }
      return acc;
    },
    { promptTokens: 0, completionTokens: 0, totalLatency: 0, latencyCount: 0 }
  );

  return {
    promptTokens: stats.promptTokens,
    completionTokens: stats.completionTokens,
    totalTokens: stats.promptTokens + stats.completionTokens,
    averageLatency:
      stats.latencyCount > 0 ? stats.totalLatency / stats.latencyCount : 0,
  };
};

const WhatsappConversation: React.FC<WhatsappConversationProps> = ({
  messages = [],
  phoneNumber,
  whatsappPhonenumberId,
  formatDate,
}) => {
  const tokens = useMemo(() => calculateTokens(messages), [messages]);

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {messages && messages.length > 0 ? (
          <ChatContainer>
            {messages.map((message: ChatMessage, index: number) => (
              <MessageContainer key={index}>
                <RoleLabel
                  role={message.role}
                  variant="body2"
                  sx={{
                    color:
                      message.role === 'user'
                        ? '#FF4D6A'
                        : theme.palette.primary.main,
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                >
                  {message.role === 'user' ? 'Client' : 'Assistant'}
                </RoleLabel>
                <MessageContent variant="body1">
                  {message.message}
                </MessageContent>
                <MessageFooter>
                  {message.role === 'assistant' &&
                    message.latency !== undefined && (
                      <Typography
                        component="span"
                        sx={{
                          color: theme.palette.lightGray.text,
                          fontSize: '12px',
                        }}
                      >
                        Latency: {message.latency.toFixed(2)}ms
                      </Typography>
                    )}
                  {message.timestamp && (
                    <Typography
                      component="span"
                      sx={{
                        color: theme.palette.lightGray.text,
                        fontSize: '12px',
                        marginLeft: 'auto',
                      }}
                    >
                      {formatDate(message.timestamp)}
                    </Typography>
                  )}
                </MessageFooter>
              </MessageContainer>
            ))}
          </ChatContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Typography sx={{ color: theme.palette.lightGray.text }}>
              No chat messages available
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: '350px',
          borderLeft: '1px solid #E5E7EB',
          backgroundColor: '#FFF',
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            div: {
              borderBottom: '1px solid #E5E7EB',
              pb: 1.5,
            },
            'div:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          <DetailRow label="Phone Number" value={phoneNumber || ''} copyable />
          <DetailRow
            label="WhatsApp Phone Number ID"
            value={whatsappPhonenumberId || ''}
            copyable
          />

          <DetailRow
            label="Prompt Tokens"
            value={tokens.promptTokens.toLocaleString()}
            tooltip="Prompt tokens represent the number of tokens used in the user's messages sent to the AI model."
          />
          <DetailRow
            label="Completion Tokens"
            value={tokens.completionTokens.toLocaleString()}
            tooltip="Completion tokens represent the number of tokens used in the AI model's responses."
          />

          <DetailRow
            label="Average Latency"
            value={`${tokens.averageLatency.toFixed(2)}ms`}
            tooltip="Average response time for assistant messages"
          />

          <DetailRow label="Messages" value={messages?.length || 0} />
          <DetailRow
            label="Last Activity"
            value={
              messages?.[messages.length - 1]?.timestamp
                ? formatDate(messages[messages.length - 1].timestamp)
                : 'N/A'
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default memo(WhatsappConversation);
